import React from 'react'
import { observer } from 'mobx-react'
import { observable } from 'mobx'
import { ApiContent } from 'src/utils/getDataFromTree'
import { i18nTranslate } from 'src/utils'
import { Link } from 'react-router-dom'
import { Container, Row, Col, Button } from 'react-bootstrap'
import { MetaDecorator } from 'src/views/components'
import { BreadCrumb } from 'src/views/components/BreadCrumb'
import { EmptyProduct } from 'src/views/components/EmptyProduct'
import { pageNames } from 'src/routes/pathParams'
import { IS_BROWSER } from 'src/utils/application'
import { mysiteContainer } from 'src/models/Mysite'
import MysiteNotFound from './MysiteNotFound'
import { isEquinoxMysite } from 'src/utils/mySiteUtils'
import { contentStackContainer } from 'src/models'
import {
  getContextStackPageEntries,
  getContentStackEntries,
} from 'src/utils/contentStackUtils'
import { enableNewFeature } from 'src/utils'
import { ContentStackPage } from './ContentStackPage'
import './styles.scss'

function homeBreadcrumb() {
  return {
    label: i18nTranslate('breadCrumb.home', 'Home', {
      nameSpace: 'ssr-resource',
    }),
    value: '/',
  }
}

function CategoryProductNotFound(props) {
  return (
    <>
      <MetaDecorator
        title={`${i18nTranslate('productListPage.notFound', 'Product Not Found', {
          nameSpace: 'ssr-resource',
        })} | Nu Skin`}
        imageUrl=""
        imageAlt=""
        description={i18nTranslate(
          'pdp.noProductsText',
          'No Product(s) Found',
          { nameSpace: 'ssr-resource' }
        )}
        location={props.location}
      />
      <BreadCrumb
        breadCrumbList={[homeBreadcrumb()]}
        location={props.location}
      />
      <EmptyProduct {...props} />
    </>
  )
}

function isMissingCategoryPage(pathname, reportedMissing) {
  return (
    pathname.includes('/category/') &&
    reportedMissing &&
    enableNewFeature('enableCategoryProductNotFound')
  )
}

class NotFoundContent extends React.PureComponent {
  render() {
    const wording = {
      text: i18nTranslate(
        'notFoundPage.text',
        `THE PAGE YOU'RE LOOKING FOR ISN'T HERE.`,
        {
          nameSpace: 'ssr-resource',
        }
      ),
      emptyTitle: i18nTranslate(
        'notFoundPage.emptyTitle',
        'Currently, there are no products to list within this category. You may try others.',
        {
          nameSpace: 'ssr-resource',
        }
      ),
      goBack: i18nTranslate('notFoundPage.goBack', 'Go Back', {
        nameSpace: 'ssr-resource',
      }),
      continueShopping: i18nTranslate(
        'notFoundPage.continueShopping',
        'Continue Shopping',
        {
          nameSpace: 'ssr-resource',
        }
      ),
    }

    return (
      <Container>
        <Row
          noGutters
          className="pt-3 pt-md-5 pb-4 flex-nowrap flex-column flex-lg-row">
          <Col className="pl-lg-4">
            <div className="content-text-title font-weight-bold pb-4 pt-5 text-center">
              {wording.text}
            </div>
            <Link to="/" className="notfound-link">
              <Button variant="primary" className="btn-font-size">
                {wording.continueShopping}
              </Button>
            </Link>
          </Col>
        </Row>
      </Container>
    )
  }
}

@observer
class NotFound extends React.Component {
  static contextType = ApiContent
  @observable csPageEntry = {}
  // Stores the pathname for which getServerSideProps returned isPageFound:false.
  // Reset implicitly whenever the URL changes (compared in render).
  @observable csrNotFoundForUrl = null

  constructor(props, context) {
    super(props)
    this.context = context
    this.contextName = 'csPageEntry'
    this.csPageEntry = this.context?.[this.contextName]?.entries
    if (!this.csPageEntry) {
      this.csPageEntry = this.context?.ssrData?.[this.contextName]?.entries
    }
  }

  handlePageNotFound = () => {
    if (IS_BROWSER) {
      this.csrNotFoundForUrl = window?.location?.pathname
    }
  }

  componentDidMount() {
    // Global Payment System - Start
    // If /checkoutGps route not found, redirect to /c`heckout, or vice versa
    try {
      if (IS_BROWSER) {
        const location = window?.location?.toString()
        if (
          location?.endsWith('/checkout') ||
          location?.endsWith('/checkoutGps')
        ) {
          this.props.history?.replace(pageNames.checkout)
        }
      }
    } catch (e) {
      console.log('Unable to redirect to checkout page')
    }
    // Global Payment System - End
  }

  render() {
    const entries = getContentStackEntries({
      contextPageEntry: this.csPageEntry,
      contentStackPage: contentStackContainer?.contentStackPage,
    })

    const forceVBRendering = enableNewFeature('enableForceVBRendering')

    // Priority 1: enableForceVBRendering — always render MFE regardless of getServerSideProps
    if (forceVBRendering) {
      return <ContentStackPage {...this.props} entries={entries} />
    }

    // Collect per-app MFE server data from the appropriate context map.
    // SSR:    context.mfeAppsMap  — { [appKey]: { mfeServerData } }
    // Client: context.mfeServerDataMap — { [appKey]: mfeServerData }
    const mfeDataEntries = IS_BROWSER
      ? Object.values(this.context?.mfeServerDataMap || {}).filter(Boolean)
      : Object.values(this.context?.mfeAppsMap || {})
          .map(e => e?.mfeServerData)
          .filter(Boolean)

    // Aggregate: URL from any entry that recorded one; page status via logical OR.
    const mfeAggregateUrl = mfeDataEntries.find(e => e?._hostMeta?.url)
      ?._hostMeta?.url
    const mfePageFound = mfeDataEntries.some(
      e => e?._routing?.pageStatus === 'found'
    )
    const mfePageNotFound =
      !mfePageFound &&
      mfeDataEntries.some(e => e?._routing?.pageStatus === 'not_found')
    const mfeDataAvailable = mfeDataEntries.length > 0

    const hasActualEntry =
      contentStackContainer?.contentStackPage?.hasActualEntry
    const hasClientOnlyMicroFrontend =
      entries?.microFrontend?.some(
        mfe => mfe?.microFrontend?.enableServerRendering !== true
      ) || false

    const currentPath = IS_BROWSER
      ? window?.location?.pathname || ''
      : this.props?.location?.pathname || ''
    const mfeUrlPath = mfeAggregateUrl?.split('?')?.[0]
    // mfeAppsMap is assembled per request in the SSR layer, so a not_found
    // signal belongs to this request and the URL check is client-only.
    // On the client, a not_found result counts only when _hostMeta.url matches.
    // Product-list always sends that url. If it is missing, the client keeps the MFE.
    const mfeNotFoundForRequest =
      mfePageNotFound && (!IS_BROWSER || mfeUrlPath === currentPath)
    // Shared by the missing-category check and the legacy NotFoundContent path below.
    const csrPageNotFound =
      IS_BROWSER && this.csrNotFoundForUrl === currentPath

    // Missing /category/ pages use the catalog product-not-found view when
    // enableCategoryProductNotFound is on. Visual Builder already returned above.
    if (
      isMissingCategoryPage(
        currentPath,
        csrPageNotFound || mfeNotFoundForRequest
      )
    ) {
      return <CategoryProductNotFound {...this.props} />
    }

    // Priority 2: A URL-specific CS entry matched this page — it is a real page.
    // This covers CSR navigation where mfeServerData is stale/irrelevant.
    if (hasActualEntry) {
      return <ContentStackPage {...this.props} entries={entries} />
    }

    if (!IS_BROWSER && hasClientOnlyMicroFrontend) {
      return <ContentStackPage {...this.props} entries={entries} />
    }

    // Priority 3: CSR getServerSideProps already came back false — show NotFoundContent.
    // Only applies to the specific URL that triggered it; automatically resets on navigation.
    if (csrPageNotFound) {
      // Priority 4: getServerSideProps result known from SSR hydration — trust it strictly.
      // Only apply when mfeServerData.url matches the current location (i.e. SSR hydration,
      // not a stale value left over from a previous CSR navigation).
      // mfeServerData.url is req.originalUrl (may include query string); compare only pathname.
    } else if (IS_BROWSER && mfeUrlPath === currentPath && mfePageFound) {
      return <ContentStackPage {...this.props} entries={entries} />
    } else if (IS_BROWSER && mfeUrlPath === currentPath && mfePageNotFound) {
      // Matched not_found for a non-category URL falls through to NotFoundContent.
    } else if (mfeDataAvailable && !IS_BROWSER) {
      if (mfePageFound) {
        return <ContentStackPage {...this.props} entries={entries} />
      }
      // isPageFound is false/missing — fall through to NotFoundContent
    } else if (entries?.microFrontend?.length > 0) {
      // Priority 5: CSR navigation (mfeServerData is stale or absent) and no URL-specific
      // CS entry — render ContentStackPage so RemoteLoader can call getServerSideProps fresh.
      // Pass onPageNotFound so we can flip back to NotFoundContent if needed.
      return (
        <ContentStackPage
          {...this.props}
          entries={entries}
          onPageNotFound={this.handlePageNotFound}
        />
      )
    }

    if (IS_BROWSER && contentStackContainer?.contentStackPage?.isLoading) {
      return <></>
    }
    const notFoundTitle = i18nTranslate(
      'notFoundPage.title',
      'Nu Skin | Page Not Found',
      {
        nameSpace: 'ssr-resource',
      }
    )

    return (
      <>
        <MetaDecorator
          title={notFoundTitle}
          imageUrl=""
          imageAlt=""
          description={i18nTranslate('notFound.desc', 'Page Not Found', {
            nameSpace: 'ssr-resource',
          })}
          location={this.props}
        />
        {mysiteContainer.mysiteApiError && isEquinoxMysite() ? (
          <MysiteNotFound />
        ) : (
          <NotFoundContent {...this.props} />
        )}
      </>
    )
  }
}

export { NotFound }
export default NotFound
